<script setup lang="ts">
import { WidgetBreadcrumbs } from '~/components/widget/breadcrumbs'
import type { IResponseData } from '~/types'
import { CurrencyIcon } from '~/components/ui/currency-icon'
import type { IMoneyTransfer } from '~/components/entity/money-transfer'
import { type TAboutProductHeader, WidgetAboutPageHeader } from '~/components/widget/about-page'
import { useCurrencyProp } from '~/composables/useCurrencyProp'
import { ContentBlock } from '~/components/ui/content-block'
import { useCurrencyFormat } from '~/composables/useCurrencyFormat'
import { WidgetReviveAd } from '~/components/widget/revive-ad'
import { WidgetAttachedOrganizations } from '~/components/widget/attached-organizations'
import { WidgetSelectCurrency } from '~/components/widget/select-currency'
import { WidgetFaqList } from '~/components/widget/faq-list'
import { useSeoFromEntity } from '~/composables/useSeoFromEntity'
import { WidgetBranchesList } from '~/components/widget/branches'
import { WidgetComments } from '~/components/widget/comments'
import { WidgetRating } from '~/components/widget/rating'
import { WidgetSidebarNews } from '~/components/widget/sidebar-news'

const { t, locale } = useI18n()
const config = useRuntimeConfig()
const { pathWithoutLocale } = useLocalizedPath()

const route = useRoute()
const moneyTransferUrl = `/money-transfers/${route.params.slug}`
const { data } = await useApiWith404<IResponseData<IMoneyTransfer>>(() => moneyTransferUrl, {
  key: `money-transfer-${route.params.slug}`,
  watch: [() => route.params.slug],
  immediate: true
})

const moneyTransfer = computed(() => data.value?.data)

const baseUrl = config.public.siteUrl
useSeoFromEntity(moneyTransfer, baseUrl, locale.value)

const breadcrumbs = computed(() => [
  { title: t('common.money_transfers'), url: '/money-transfers' },
  {
    title: moneyTransfer.value?.title || t('common.money_transfers'),
    url: moneyTransfer.value?.url
  }
])

const fitMoneyTransfer = computed<TAboutProductHeader>(() => ({
  title: moneyTransfer.value?.title ?? '',
  organization: {
    official_title: moneyTransfer.value?.transfer_operator ?? '',
    url: '',
    status: '',
    status_comments: ''
  },
  organizations: moneyTransfer.value?.organizations ?? [],
  views_count: moneyTransfer.value?.views_count ?? 0,
  updated_at: moneyTransfer.value?.updated_at ?? '',
  logo: moneyTransfer.value?.logo ?? '',
  id: moneyTransfer.value?.id ?? 0,
  status: moneyTransfer.value?.status,
  status_comments: moneyTransfer.value?.status_comments,
  commissions: moneyTransfer.value?.commissions,
  url: moneyTransfer.value?.url ?? '',
  description: moneyTransfer.value?.description ?? '',
  call_center_phone: '',
  amount_from: ''
}))
</script>

<template>
  <Main>
    <Container>
      <WidgetBreadcrumbs :links="breadcrumbs" />
      <ContentLayout>
        <template #right-sidebar>
          <aside>
            <WidgetSelectCurrency />
            <WidgetReviveAd zoneid="27" class="overflow-hidden rounded-lg" />
            <WidgetAttachedOrganizations
              v-if="moneyTransfer && moneyTransfer.organizations.length"
              :heading="t('page.money-transfers.slug.partner_banks')"
              :intro="t('page.money-transfers.slug.partner_banks_intro')"
              :organizations="moneyTransfer.organizations"
            />
            <WidgetSidebarNews
              v-if="moneyTransfer && moneyTransfer.news_count"
              :heading="t('page.news.related_news')"
              path="/news"
              variant="white"
              :query-params="{
                per_page: '5',
                'filter[organization_id]': moneyTransfer.id,
                'filter[organization_type]': 'money_transfer'
              }"
            />
          </aside>
        </template>
        <div>
          <div class="space-y-5" role="main">
            <WidgetAboutPageHeader
              v-if="moneyTransfer"
              type="money-transfer"
              :product="fitMoneyTransfer"
            >
              <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 md:gap-2">
                <div v-if="moneyTransfer && moneyTransfer.registration_date">
                  <div class="SecondaryTextHeadings">
                    {{ t('page.money-transfers.slug.registration_date') }}
                  </div>
                  <div class="font-semibold">
                    {{ useDateFormat(moneyTransfer.registration_date) }}
                  </div>
                </div>
                <div v-if="moneyTransfer && moneyTransfer.billing_center">
                  <div class="SecondaryTextHeadings">
                    {{ t('page.money-transfers.slug.billing_center') }}
                  </div>
                  <div class="font-semibold">
                    {{ moneyTransfer.billing_center }}
                  </div>
                </div>
                <div v-if="moneyTransfer && moneyTransfer.website_url">
                  <div class="SecondaryTextHeadings">
                    {{ t('page.money-transfers.slug.website') }}
                  </div>
                  <a
                    :href="moneyTransfer.website_url"
                    class="text-sm text-blue-600 hover:underline"
                    target="_blank"
                    rel="noopener"
                  >
                    {{ t('page.money-transfers.slug.go_to_website') }}
                  </a>
                </div>
                <div v-if="moneyTransfer && moneyTransfer.registration_number_nbkr">
                  <div class="SecondaryTextHeadings">
                    {{ t('page.money-transfers.slug.nbkr_registration_number') }}
                  </div>
                  <div class="font-semibold">
                    {{ moneyTransfer.registration_number_nbkr }}
                  </div>
                </div>
                <div v-if="moneyTransfer && moneyTransfer.transfer_operator">
                  <div class="SecondaryTextHeadings">
                    {{ t('page.money-transfers.slug.transfer_operator') }}
                  </div>
                  <div class="font-semibold">
                    {{ moneyTransfer.transfer_operator }}
                  </div>
                </div>
              </div>
            </WidgetAboutPageHeader>

            <section v-if="moneyTransfer?.description">
              <ContentBlock variant="white">
                <Content>
                  <div v-hydrated-html="moneyTransfer?.description" />
                </Content>
              </ContentBlock>
            </section>

            <section v-if="moneyTransfer && moneyTransfer.commissions.length">
              <ContentBlock
                variant="white"
                :heading-text="
                  t('page.money-transfers.slug.commissions_heading', { title: moneyTransfer.title })
                "
                heading-tag="h2"
                heading-class="h4"
              >
                <div class="overflow-hidden overflow-x-auto rounded-lg">
                  <table class="w-full border-separate border-spacing-0">
                    <thead class="bg-gray-100 text-left font-semibold">
                      <tr>
                        <th class="rounded-tl-lg border-l border-t border-gray-200 px-3 py-4">
                          {{ t('page.money-transfers.slug.transfer_currency') }}
                        </th>
                        <th class="border-l border-t border-gray-200 px-3 py-4">
                          {{ t('page.money-transfers.slug.min') }}
                        </th>
                        <th class="border-l border-t border-gray-200 px-3 py-4">
                          {{ t('page.money-transfers.slug.max') }}
                        </th>
                        <th
                          class="rounded-tr-lg border-l border-r border-t border-gray-200 px-3 py-4"
                        >
                          {{ t('page.money-transfers.slug.commission') }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(item, index) in moneyTransfer.commissions" :key="item.id">
                        <td
                          :class="[
                            {
                              'rounded-bl-lg': index === moneyTransfer.commissions.length - 1
                            },
                            'border-b border-l border-gray-200 px-3 py-4'
                          ]"
                        >
                          <div class="flex items-center gap-2">
                            <CurrencyIcon
                              :code="useCurrencyProp(item.currency_id, 'code') || ''"
                              :title="useCurrencyProp(item.currency_id, 'code') || ''"
                              size="lg"
                              country
                            />
                            <span>{{ useCurrencyProp(item.currency_id, 'code') }}</span>
                          </div>
                        </td>
                        <td class="whitespace-nowrap border-b border-l border-gray-200 px-3 py-4">
                          <template v-if="item.amount_from">{{
                            useCurrencyFormat(item.amount_from, item.currency_id)
                          }}</template>
                        </td>
                        <td class="whitespace-nowrap border-b border-l border-gray-200 px-3 py-4">
                          <template v-if="item.amount_from">{{
                            useCurrencyFormat(item.amount_to, item.currency_id)
                          }}</template>
                        </td>
                        <td
                          :class="[
                            {
                              'rounded-br-lg': index === moneyTransfer.commissions.length - 1
                            },
                            'whitespace-nowrap border-b border-l border-r border-gray-200 px-3 py-4'
                          ]"
                        >
                          <template v-if="item.type === 'fix'">
                            {{ useCurrencyFormat(item.value, item.currency_id) }}
                          </template>
                          <template v-else> {{ item.value }}%</template>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </ContentBlock>
            </section>

            <section>
              <WidgetBranchesList
                v-if="moneyTransfer"
                :organization="moneyTransfer"
                :hide-organization-details="true"
                :hide-pagination="false"
              />
            </section>

            <WidgetRating
              v-if="moneyTransfer && config.public.widgetRatingEnabled"
              :entity="moneyTransfer"
              entity-type="money_transfer"
              :heading-text="
                $t('page.money_transfer.slug.rating_heading', { title: moneyTransfer.title })
              "
            />

            <WidgetComments
              v-if="moneyTransfer"
              commentable-type="money_transfer"
              :commentable-id="moneyTransfer.id"
            />

            <WidgetFaqList
              :query-params="{
                'filter[faqable_url]': pathWithoutLocale
              }"
            />
          </div>
        </div>
      </ContentLayout>
    </Container>
  </Main>
</template>
